// src/components/settings/ContentSourcesTab.jsx - FIXED VERSION
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {contentCacheAPI, settingsAPI} from '../../services/api';
import {
    PlusIcon,
    TrashIcon,
    GlobeAltIcon,
    LinkIcon,
    ArrowPathIcon,
    ClockIcon,
    DocumentTextIcon,
    EyeIcon,
    SparklesIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ShieldExclamationIcon,
    ExclamationCircleIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

function ContentSourcesTab() {
    const [newSource, setNewSource] = useState('');
    const [selectedContent, setSelectedContent] = useState(null);
    const [activeTab, setActiveTab] = useState('sources'); // 'sources' or 'cached'
    const queryClient = useQueryClient();
    const [urlValidation, setUrlValidation] = useState(null);
    const [showBlocked, setShowBlocked] = useState(false);
    const [showHiddenContent, setShowHiddenContent] = useState(false);
    // Fetch content sources with better error handling
    const { data: sources, isLoading: sourcesLoading, error: sourcesError } = useQuery(
        'contentSources',
        () => settingsAPI.getContentSources(),
        {
            retry: 1,
            onError: (error) => {
                console.error('Content sources error:', error);
                if (error.response?.status === 401) {
                    toast.error('Please log in again');
                }
            }
        }
    );

    // Fetch cached content with proper auth headers
    const { data: cachedContent, isLoading: cacheLoading, error: cacheError } = useQuery(
        'cachedContent',
        async () => {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/content-cache', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Authentication required');
                }
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return response.json();
        },
        {
            refetchInterval: 30000, // Refresh every 30 seconds
            retry: 1,
            onError: (error) => {
                console.error('Cached content error:', error);
                if (error.message.includes('Authentication')) {
                    toast.error('Please log in again');
                }
            }
        }
    );

    const { data: moderationStats } = useQuery(
        'moderationStats',
        async () => {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/content-cache/moderation/stats', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok) throw new Error('Failed to fetch moderation stats');
            return response.json();
        },
        {
            refetchInterval: 60000, // Refresh every minute
            retry: 1
        }
    );

    // Fetch blocked content
    const { data: blockedContent, error: blockedError, isLoading: blockedLoading } = useQuery(
        'blockedContent',
        async () => {
            const response = await contentCacheAPI.getBlocked();
            console.log(response);
            return response.data;
        },
        {
            enabled: showBlocked,
            retry: (failureCount, error) => {
                if (error?.response?.status === 404) return false;
                return failureCount < 1;
            }
        }
    );

    // Add this check in your render:
    if (blockedError && blockedError?.response?.status !== 404) {
        console.error('Error fetching blocked content:', blockedError);
    }

    // URL validation mutation
    const validateUrlMutation = useMutation(
        async (url) => {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/content-cache/validate-url', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ url })
            });
            if (!response.ok) throw new Error('Failed to validate URL');
            return response.json();
        },
        {
            onSuccess: (data) => {
                setUrlValidation(data.data);
            },
            onError: () => {
                setUrlValidation(null);
            }
        }
    );

    // Update sources mutation
    const updateSourcesMutation = useMutation(settingsAPI.saveContentSources, {
        onSuccess: () => {
            queryClient.invalidateQueries('contentSources');
            toast.success('Content sources updated!');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to update sources');
        },
    });

    // Cache content mutation with proper auth
    const cacheContentMutation = useMutation(
        async (sources) => {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/content-cache/refresh', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ sources })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return response.json();
        },
        {
            onSuccess: (data) => {
                queryClient.invalidateQueries('cachedContent');
                if (data.success) {
                    toast.success(`Content caching started successfully!`);
                } else {
                    toast.success('Content caching started in background');
                }
            },
            onError: (error) => {
                toast.error('Failed to cache content: ' + error.message);
            }
        }
    );

    // Generate post with specific content
    const generateWithContentMutation = useMutation(
        async (contentId) => {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/posts/generate-with-content', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ contentId })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return response.json();
        },
        {
            onSuccess: () => {
                toast.success('Post generation started with selected content!');
            },
            onError: (error) => {
                toast.error('Failed to generate post: ' + error.message);
            }
        }
    );

    const toggleContentVisibilityMutation = useMutation(
        async ({ contentId, isActive }) => {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/content-cache/${contentId}/visibility`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ isActive })
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            return response.json();
        },
        {
            onSuccess: (data, variables) => {
                queryClient.invalidateQueries('cachedContent');
                toast.success(variables.isActive ? 'Content restored' : 'Content hidden');
            },
            onError: (error) => {
                toast.error('Failed to update content: ' + error.message);
            }
        }
    );

    const handleAddSource = () => {
        if (!newSource.trim()) {
            toast.error('Please enter a valid URL');
            return;
        }

        try {
            new URL(newSource); // Validate URL
        } catch {
            toast.error('Please enter a valid URL (include https://)');
            return;
        }

        // Check if URL is blocked before adding
        if (urlValidation?.isBlocked) {
            toast.error(`This domain is blocked due to content policy: ${urlValidation.domain}`);
            return;
        }

        if (!urlValidation?.isValid) {
            toast.error('Please enter a valid URL format');
            return;
        }

        const currentSources = sources?.data?.data || sources?.data || [];
        const safeCurrentSources = Array.isArray(currentSources) ? currentSources : [];
        if (safeCurrentSources.includes(newSource)) {
            toast.error('This URL is already in your list');
            return;
        }

        const updatedSources = [...safeCurrentSources, newSource];
        updateSourcesMutation.mutate({ sources: updatedSources });
        setNewSource('');
        setUrlValidation(null); // Clear validation
    };

    // Add this function to handle URL input changes
    const handleUrlInputChange = (e) => {
        const url = e.target.value;
        setNewSource(url);

        // Clear previous validation
        setUrlValidation(null);

        // Validate URL if it looks complete
        if (url.length > 10 && url.includes('.')) {
            try {
                new URL(url);
                validateUrlMutation.mutate(url);
            } catch {
                // Invalid URL format, don't validate yet
            }
        }
    };

    const handleRemoveSource = (indexToRemove) => {
        const currentSources = sources?.data?.data || sources?.data || [];
        const safeCurrentSources = Array.isArray(currentSources) ? currentSources : [];
        const updatedSources = safeCurrentSources.filter((_, index) => index !== indexToRemove);

        if (updatedSources.length === 0) {
            toast.error('You must have at least one content source');
            return;
        }

        updateSourcesMutation.mutate({ sources: updatedSources });
    };

    const handleCacheContent = () => {
        const sourcesList = sources?.data?.data || sources?.data || [];
        const safeSourcesList = Array.isArray(sourcesList) ? sourcesList : [];
        if (safeSourcesList.length === 0) {
            toast.error('Add some content sources first');
            return;
        }
        cacheContentMutation.mutate(safeSourcesList);
    };

    const handleGenerateWithContent = (contentId) => {
        generateWithContentMutation.mutate(contentId);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleAddSource();
        }
    };

    const formatTimeAgo = (date) => {
        const now = new Date();
        const diff = now - new Date(date);
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(hours / 24);

        if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
        if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        return 'Just now';
    };

    const isContentStale = (date, maxHours = 24) => {
        const diff = new Date() - new Date(date);
        return diff > (maxHours * 60 * 60 * 1000);
    };

    if (sourcesLoading) {
        return <div className="animate-pulse">Loading content sources...</div>;
    }

    // FIXED: Better error handling
    if (sourcesError) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="text-red-600 mb-2">
                    Failed to load content sources: {sourcesError.message}
                </div>
                <button
                    onClick={() => queryClient.invalidateQueries('contentSources')}
                    className="text-sm text-red-600 hover:text-red-500"
                >
                    Try again
                </button>
            </div>
        );
    }

    // FIXED: Correct data extraction for nested API response structure
    // API returns: { data: { success: true, data: [...] } }
    const sourcesList = sources?.data?.data || sources?.data || [];
    const cachedList = cachedContent?.data || [];

    // Safety check to ensure sourcesList is always an array
    const safeSourcesList = Array.isArray(sourcesList) ? sourcesList : [];
    const safeCachedList = Array.isArray(cachedList) ? cachedList : [];

    // console.log('Content sources data:', sources);
    // console.log('Sources list:', sourcesList);
    // console.log('Sources list type:', typeof sourcesList, 'isArray:', Array.isArray(sourcesList));
    // console.log('Safe sources list:', safeSourcesList);
    // console.log('Cached content data:', cachedContent);
    // console.log('Cached list:', cachedList);

    // Add this after your existing mutations (around line 200)


    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-gray-900">Content Sources & Cache</h3>
                <p className="mt-1 text-sm text-gray-600">
                    Manage your content sources and cached content for AI post generation
                </p>
            </div>

            {/* Debug info */}
            {/*<div className="bg-gray-50 p-4 rounded-lg">*/}
            {/*    <h4 className="text-sm font-medium text-gray-700 mb-2">Debug Info:</h4>*/}
            {/*    <div className="text-xs text-gray-600 space-y-1">*/}
            {/*        <p>Sources loading: {sourcesLoading.toString()}</p>*/}
            {/*        <p>Sources error: {sourcesError?.message || 'None'}</p>*/}
            {/*        <p>Sources data: {JSON.stringify(sources)}</p>*/}
            {/*        <p>Sources list length: {safeSourcesList.length}</p>*/}
            {/*        <p>Cache loading: {cacheLoading.toString()}</p>*/}
            {/*        <p>Cache error: {cacheError?.message || 'None'}</p>*/}
            {/*        <p>Cache list length: {safeCachedList.length}</p>*/}
            {/*    </div>*/}
            {/*</div>*/}

            {/* Tab Navigation */}
            {/* Updated Tab Navigation - replace your existing tab navigation */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('sources')}
                        className={`${
                            activeTab === 'sources'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                    >
                        <GlobeAltIcon className="h-5 w-5 mr-2" />
                        Sources ({safeSourcesList.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('cached')}
                        className={`${
                            activeTab === 'cached'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                    >
                        <DocumentTextIcon className="h-5 w-5 mr-2" />
                        Cached Content ({safeCachedList.length})
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab('blocked');
                            setShowBlocked(true);
                        }}
                        className={`${
                            activeTab === 'blocked'
                                ? 'border-red-500 text-red-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                    >
                        <ShieldExclamationIcon className="h-5 w-5 mr-2" />
                        Blocked ({moderationStats?.data?.blockedContent || 0})
                    </button>
                </nav>
            </div>

            {/* Add this new tab after your existing cached content tab */}
            {activeTab === 'blocked' && (
                <div className="space-y-6">
                    {/* Moderation Overview */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h4 className="text-md font-medium text-gray-900 mb-4">Content Moderation Overview</h4>

                        {moderationStats?.data && (
                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-gray-600">{moderationStats.data.totalContent}</div>
                                    <div className="text-sm text-gray-600">Total Content</div>
                                </div>
                                <div className="bg-green-50 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-green-600">{moderationStats.data.activeContent}</div>
                                    <div className="text-sm text-green-600">Approved</div>
                                </div>
                                <div className="bg-red-50 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-red-600">{moderationStats.data.blockedContent}</div>
                                    <div className="text-sm text-red-600">Blocked</div>
                                </div>
                                <div className="bg-yellow-50 p-4 rounded-lg">
                                    <div className="text-2xl font-bold text-yellow-600">{moderationStats.data.totalWithErrors}</div>
                                    <div className="text-sm text-yellow-600">With Errors</div>
                                </div>
                            </div>
                        )}

                        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-blue-800">Content Moderation Active</h3>
                                    <div className="mt-2 text-sm text-blue-700">
                                        <p>Our content moderation system automatically filters:</p>
                                        <ul className="list-disc list-inside mt-1">
                                            <li>Explicit keywords and adult content</li>
                                            <li>Blacklisted domains and pornographic sites</li>
                                            <li>Inappropriate or harmful material</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Blocked Content List */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h4 className="text-md font-medium text-gray-900 mb-4">Blocked Content</h4>

                        {blockedContent?.data?.length === 0 ? (
                            <div className="text-center py-8">
                                <ShieldExclamationIcon className="mx-auto h-12 w-12 text-green-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No blocked content</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    All your content sources passed moderation checks.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {blockedContent?.data?.map((content) => (
                                    <div
                                        key={content._id}
                                        className="border border-red-200 bg-red-50 rounded-lg p-4"
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center space-x-2 mb-2">
                                                    <ShieldExclamationIcon className="h-5 w-5 text-red-500" />
                                                    <h5 className="text-sm font-medium text-red-900">
                                                        {content.title}
                                                    </h5>
                                                </div>

                                                <p className="text-sm text-red-700 mb-2">
                                                    <strong>Source:</strong> {content.sourceUrl}
                                                </p>

                                                {content.metadata?.moderationReasons && (
                                                    <div className="mb-2">
                                                        <span className="text-sm font-medium text-red-800">Blocked for:</span>
                                                        <div className="flex flex-wrap gap-1 mt-1">
                                                            {content.metadata.moderationReasons.map(reason => (
                                                                <span key={reason} className="px-2 py-1 bg-red-200 text-red-800 text-xs rounded">
                                                                    {reason.replace(/_/g, ' ').toLowerCase()}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="text-xs text-red-600">
                                                    Blocked: {formatTimeAgo(content.metadata?.moderationChecked || content.createdAt)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'sources' && (
                <div className="space-y-6">
                    {/* Add New Source */}
                    {/* Updated Add New Source section - replace your existing one */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h4 className="text-md font-medium text-gray-900 mb-4">Add New Source</h4>
                        <div className="space-y-3">
                            <div className="flex space-x-3">
                                <div className="flex-1">
                                    <input
                                        type="url"
                                        value={newSource}
                                        onChange={handleUrlInputChange}
                                        onKeyPress={handleKeyPress}
                                        placeholder="https://example.com/page"
                                        className={`block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                                            urlValidation?.isBlocked ? 'border-red-300' :
                                                urlValidation?.isValid === false ? 'border-red-300' :
                                                    urlValidation?.isValid ? 'border-green-300' : 'border-gray-300'
                                        }`}
                                    />
                                </div>
                                <button
                                    onClick={handleAddSource}
                                    disabled={updateSourcesMutation.isLoading || urlValidation?.isBlocked || urlValidation?.isValid === false}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                >
                                    <PlusIcon className="h-4 w-4 mr-2" />
                                    Add
                                </button>
                            </div>

                            {/* URL Validation Messages */}
                            {urlValidation && (
                                <div className="space-y-2">
                                    {urlValidation.isBlocked && (
                                        <div className="flex items-center text-sm text-red-600">
                                            <ExclamationCircleIcon className="h-4 w-4 mr-2" />
                                            This domain is blocked due to content policy: {urlValidation.domain}
                                        </div>
                                    )}
                                    {urlValidation.isValid === false && (
                                        <div className="flex items-center text-sm text-red-600">
                                            <ExclamationCircleIcon className="h-4 w-4 mr-2" />
                                            Invalid URL format. Please include https:// and a valid domain.
                                        </div>
                                    )}
                                    {urlValidation.isValid && !urlValidation.isBlocked && (
                                        <div className="flex items-center text-sm text-green-600">
                                            <CheckCircleIcon className="h-4 w-4 mr-2" />
                                            URL looks good and is not blocked
                                        </div>
                                    )}
                                </div>
                            )}

                            {validateUrlMutation.isLoading && (
                                <div className="flex items-center text-sm text-blue-600">
                                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                                    Validating URL...
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Current Sources */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-md font-medium text-gray-900">
                                Current Sources ({safeSourcesList.length})
                            </h4>
                            {safeSourcesList.length > 0 && (
                                <button
                                    onClick={handleCacheContent}
                                    disabled={cacheContentMutation.isLoading}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                >
                                    {cacheContentMutation.isLoading ? (
                                        <>
                                            <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                                            Caching...
                                        </>
                                    ) : (
                                        <>
                                            <ArrowPathIcon className="h-4 w-4 mr-2" />
                                            Cache All Content
                                        </>
                                    )}
                                </button>
                            )}
                        </div>

                        {safeSourcesList.length === 0 ? (
                            <div className="text-center py-8">
                                <GlobeAltIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No content sources</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Add your first content source to start generating posts.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {safeSourcesList.map((source, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                    >
                                        <div className="flex items-center space-x-3 flex-1">
                                            <GlobeAltIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center space-x-2">
                                                    <a
                                                        href={source}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-sm font-medium text-blue-600 hover:text-blue-500 truncate"
                                                    >
                                                        {source}
                                                    </a>
                                                    <LinkIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleRemoveSource(index)}
                                            disabled={updateSourcesMutation.isLoading}
                                            className="inline-flex items-center p-2 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 disabled:opacity-50 ml-3"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'cached' && (
                <div className="space-y-6">
                    {/* Cache Status */}
                    {/* Updated Cache Status section - replace your existing one */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-md font-medium text-gray-900">Cache Status</h4>
                            <button
                                onClick={() => queryClient.invalidateQueries('cachedContent')}
                                className="text-sm text-blue-600 hover:text-blue-500"
                            >
                                <ArrowPathIcon className="h-4 w-4 inline mr-1" />
                                Refresh
                            </button>
                        </div>

                        {cacheError && (
                            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                                <div className="text-red-600 text-sm">
                                    Cache error: {cacheError.message}
                                </div>
                            </div>
                        )}

                        {/* Moderation Status */}
                        {moderationStats?.data && (
                            <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-3">
                                <div className="flex items-center text-sm text-green-700">
                                    <ShieldExclamationIcon className="h-4 w-4 mr-2" />
                                    Content moderation is active - {moderationStats.data.blockedContent} sources blocked
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-4 gap-4">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <div className="text-2xl font-bold text-blue-600">{safeCachedList.length}</div>
                                <div className="text-sm text-blue-600">Total Cached</div>
                            </div>
                            <div className="bg-green-50 p-4 rounded-lg">
                                <div className="text-2xl font-bold text-green-600">
                                    {safeCachedList.filter(item => !isContentStale(item.metadata?.lastFetched)).length}
                                </div>
                                <div className="text-sm text-green-600">Fresh Content</div>
                            </div>
                            <div className="bg-yellow-50 p-4 rounded-lg">
                                <div className="text-2xl font-bold text-yellow-600">
                                    {safeCachedList.filter(item => isContentStale(item.metadata?.lastFetched)).length}
                                </div>
                                <div className="text-sm text-yellow-600">Stale Content</div>
                            </div>
                            <div className="bg-red-50 p-4 rounded-lg">
                                <div className="text-2xl font-bold text-red-600">
                                    {cachedContent?.hiddenCount || 0}
                                </div>
                                <div className="text-sm text-red-600">Hidden Content</div>
                            </div>
                        </div>
                    </div>

                    {/* Cached Content List */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h4 className="text-md font-medium text-gray-900 mb-4">Cached Content</h4>

                        {cacheLoading ? (
                            <div className="animate-pulse space-y-4">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="h-20 bg-gray-200 rounded"></div>
                                ))}
                            </div>
                        ) : safeCachedList.length === 0 ? (
                            <div className="text-center py-8">
                                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No cached content</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Cache content from your sources to enable manual post creation.
                                </p>
                                <button
                                    onClick={handleCacheContent}
                                    disabled={safeSourcesList.length === 0}
                                    className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                                >
                                    <ArrowPathIcon className="h-4 w-4 mr-2" />
                                    Cache Content Now
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {safeCachedList
                                    .filter(item => showHiddenContent || item.isActive !== false)
                                    .map((item, index) => (
                                        <div key={item._id} className={`p-4 border rounded-lg hover:bg-gray-50 ${
                                            item.isActive === false ? 'bg-red-50 border-red-200' : 'border-gray-200'
                                        }`}>
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <h5 className="text-sm font-medium text-gray-900 truncate">
                                                            {item.title}
                                                        </h5>
                                                        {item.isActive === false && (
                                                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-red-100 text-red-800">
                            Hidden
                        </span>
                                                        )}
                                                        {isContentStale(item.metadata?.lastFetched) && (
                                                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-yellow-100 text-yellow-800">
                            Stale
                        </span>
                                                        )}
                                                    </div>

                                                    {/* Rest of your existing content display */}
                                                    <p className="text-xs text-gray-500 mb-2">
                                                        {item.sourceUrl}
                                                    </p>
                                                    <p className="text-sm text-gray-600 line-clamp-2">
                                                        {item.summary || item.content?.substring(0, 150) + '...'}
                                                    </p>

                                                    <div className="flex items-center text-xs text-gray-500 mt-2 space-x-4">
                    <span>
                        {item.metadata?.lastFetched ?
                            `Cached ${new Date(item.metadata.lastFetched).toLocaleDateString()}` :
                            'No cache date'
                        }
                    </span>
                                                        <span>Used {item.usageCount || 0} times</span>
                                                        {item.metadata?.contentLength && (
                                                            <span>{item.metadata.contentLength} chars</span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Action buttons */}
                                                <div className="flex items-center space-x-2 ml-4">
                                                    {item.isActive !== false && (
                                                        <button
                                                            onClick={() => generateWithContentMutation.mutate(item._id)}
                                                            disabled={generateWithContentMutation.isLoading}
                                                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 disabled:opacity-50"
                                                        >
                                                            Generate Post
                                                        </button>
                                                    )}

                                                    <button
                                                        onClick={() => toggleContentVisibilityMutation.mutate({
                                                            contentId: item._id,
                                                            isActive: item.isActive === false
                                                        })}
                                                        disabled={toggleContentVisibilityMutation.isLoading}
                                                        className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded ${
                                                            item.isActive === false
                                                                ? 'text-green-700 bg-green-100 hover:bg-green-200'
                                                                : 'text-red-700 bg-red-100 hover:bg-red-200'
                                                        } disabled:opacity-50`}
                                                    >
                                                        {item.isActive === false ? 'Restore' : 'Remove'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>


                </div>
            )}

            {/* Usage Tips */}
            {/* Updated Usage Tips - replace your existing one */}
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex">
                    <div className="flex-shrink-0">
                        <span className="text-green-400 text-lg">✨</span>
                    </div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">Content Cache & Moderation Benefits</h3>
                        <div className="mt-2 text-sm text-green-700">
                            <ul className="list-disc list-inside space-y-1">
                                <li><strong>Faster Generation:</strong> Cached content loads instantly vs fetching each time</li>
                                <li><strong>Manual Selection:</strong> Choose specific content pieces for targeted posts</li>
                                <li><strong>Better Quality:</strong> Review and select the best content for your brand</li>
                                <li><strong>Content Safety:</strong> Automatic filtering blocks inappropriate content and sites</li>
                                <li><strong>Offline Capability:</strong> Generate posts even when sources are temporarily unavailable</li>
                                <li><strong>Usage Tracking:</strong> See which content performs best</li>
                                <li><strong>Brand Protection:</strong> Moderation ensures your content stays professional</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ContentSourcesTab;